import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { profileService } from '@/services/profileService'
import { acceptInvitation } from '@/services/teamService'
import { identifyUser, resetUser, setOrganization } from '@/lib/posthog'
import { setSentryUser, clearSentryUser } from '@/lib/sentry'

interface Profile {
  id: string
  organization_id: string | null
  full_name: string | null
}

interface AuthContextType {
  user: User | null
  session: Session | null
  profile: Profile | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  // Fetch user profile and set analytics context
  const fetchProfile = async (authUser: User) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, organization_id, full_name')
        .eq('id', authUser.id)
        .single()
      if (error) {
        console.error('Error fetching profile:', error)
        return
      }
      setProfile(data)

      // Identify user in PostHog & Sentry
      identifyUser(authUser.id, {
        email: authUser.email,
        name: data.full_name ?? undefined,
        organization_id: data.organization_id ?? undefined,
      })
      setSentryUser({
        id: authUser.id,
        email: authUser.email,
        organization_id: data.organization_id ?? undefined,
      })
      if (data.organization_id) {
        setOrganization(data.organization_id)
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err)
    }
  }


  const autoAcceptPendingInvite = async (user: User) => {
    if (!user.email) return

    try {
      const { data: invitation } = await supabase
        .from('team_invitations')
        .select('token')
        .eq('email', user.email.toLowerCase())
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (!invitation?.token) return

      await acceptInvitation(invitation.token)
      await fetchProfile(user)
    } catch (err) {
      console.warn('Auto-accept pending invite skipped:', err)
    }
  }

  useEffect(() => {
    let cancelled = false

    const initSession = async (authUser: User) => {
      await fetchProfile(authUser)
      if (cancelled) return
      autoAcceptPendingInvite(authUser).catch(() => {})
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        initSession(session.user).finally(() => {
          if (!cancelled) setLoading(false)
        })
      } else {
        setLoading(false)
      }
    }).catch((err) => {
      if (cancelled) return
      console.error('Failed to get auth session:', err)
      setLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        initSession(session.user).finally(() => {
          if (!cancelled) setLoading(false)
        })
      } else {
        setProfile(null)
        setLoading(false)
      }
    })

    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error: error as Error | null }
  }

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin + "/dashboard",
        data: { full_name: fullName },
      },
    })

    // Supabase returns a fake user with empty identities for existing emails
    if (data?.user && data.user.identities?.length === 0) {
      return { error: new Error('An account with this email already exists. Please sign in or use forgot password.') as Error }
    }

    return { error: error as Error | null }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setSession(null)
    setProfile(null)
    profileService.clearOrgIdCache()
    resetUser()
    clearSentryUser()
  }

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
